import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFreelancerProjectsEnabled } from "@/hooks/use-freelancer-projects";
import { useCanLinkFreelanceSuppliers, useFreelanceSupplierOverview } from "@/hooks/use-freelancer-suppliers";
import { FreelanceSupplierRow } from "@/components/freelancers/FreelanceSupplierRow";
import type { FreelanceSupplierStatus } from "@/lib/db-types";

type Tab = FreelanceSupplierStatus | "all";

const TABS: { value: Tab; label: string }[] = [
  { value: "unlinked", label: "Ukoblede" },
  { value: "linked", label: "Koblet" },
  { value: "not_freelancer", label: "Ikke frilanser" },
  { value: "all", label: "Alle" },
];

/**
 * /frilansere/leverandorer (VIB-2927): leverandører med fakturaer på prosjekter.
 * Kobles de til en frilanser, fylles prosjekthistorikken bakover.
 */
export default function FreelanceSuppliersPage() {
  const enabled = useFreelancerProjectsEnabled();
  const { data: rows = [], isLoading, error } = useFreelanceSupplierOverview(enabled);
  const canLink = useCanLinkFreelanceSuppliers(enabled);
  const [tab, setTab] = useState<Tab>("unlinked");
  const [search, setSearch] = useState("");

  const counts = useMemo(() => {
    const c: Record<Tab, number> = { unlinked: 0, linked: 0, not_freelancer: 0, all: rows.length };
    for (const r of rows) c[r.status] += 1;
    return c;
  }, [rows]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => (tab === "all" || r.status === tab) && (!q || r.supplier_name.toLowerCase().includes(q)));
  }, [rows, tab, search]);

  if (!enabled) return <div className="page-container"><p className="text-muted-foreground">Ikke tilgjengelig ennå.</p></div>;

  return (
    <div className="page-container animate-fade-in space-y-6">
      <Link to="/frilansere" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
        <SrIcon name="arrow-left" className="h-4 w-4" />
        Frilansere
      </Link>
      <div>
        <h1 className="font-display text-[clamp(28px,3vw,40px)] font-semibold tracking-[-0.01em]">Leverandører</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Leverandører med fakturaer på prosjekter. Koble en leverandør til en frilanser, så vises alle fakturaene fra den på profilen, også de gamle.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
              <TabsList>
                {TABS.map((t) => (
                  <TabsTrigger key={t.value} value={t.value}>{t.label} · {counts[t.value]}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Input
              className="max-w-xs"
              placeholder="Søk leverandør…"
              aria-label="Søk leverandør"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {error ? (
            <p className="text-destructive">Feil ved lasting av leverandører: {(error as Error).message}</p>
          ) : isLoading ? (
            <div className="flex justify-center py-8"><SrIcon name="loader2" className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : visible.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              {tab === "unlinked" ? "Alle leverandørene er gått gjennom." : "Ingen leverandører her."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Leverandør og linjer</th>
                    <th className="px-3 py-2 font-medium">Omfang</th>
                    <th className="px-3 py-2 text-right font-medium">Fakturert</th>
                    <th className="px-3 py-2 font-medium">Siste</th>
                    <th className="px-3 py-2 font-medium">Frilanser</th>
                    <th className="px-3 py-2"><span className="sr-only">Handlinger</span></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <FreelanceSupplierRow key={r.po_supplier_id ?? `n:${r.supplier_key}`} row={r} canLink={canLink} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
